import { spawn } from "node:child_process";

export const DEVICE_GRANT_TYPE = "urn:ietf:params:oauth:grant-type:device_code";

const REQUEST_TIMEOUT_MS = 30_000;

export interface DeviceAuthorization {
  device_code: string;
  user_code: string;
  verification_uri: string;
  verification_uri_complete?: string;
  expires_in: number;
  interval?: number;
}

interface DeviceToken {
  access_token: string;
  token_type: string;
  scope?: string;
}

interface OAuthError {
  error?: string;
  error_description?: string;
}

export interface DeviceFlowOptions {
  apiUrl: string;
  clientId: "q5m-cli" | "q5m-pi" | "q5m-agent-extension";
  fetch?: typeof globalThis.fetch;
  sleep?: (milliseconds: number, signal?: AbortSignal) => Promise<void>;
  now?: () => number;
  signal?: AbortSignal;
  onVerification: (authorization: DeviceAuthorization) => void | Promise<void>;
}

export class DeviceAuthorizationError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = "DeviceAuthorizationError";
  }
}
const defaultSleep = (
  milliseconds: number,
  signal?: AbortSignal,
): Promise<void> => {
  signal?.throwIfAborted();
  const { promise, resolve, reject } = Promise.withResolvers<void>();
  const onAbort = (): void => {
    clearTimeout(timer);
    reject(signal?.reason);
  };
  const timer = setTimeout(() => {
    signal?.removeEventListener("abort", onAbort);
    resolve();
  }, milliseconds);
  signal?.addEventListener("abort", onAbort, { once: true });
  if (signal?.aborted) onAbort();
  return promise;
};

function boundedRequestSignal(
  callerSignal: AbortSignal | undefined,
  timeoutMs: number,
): AbortSignal {
  const timeoutSignal = AbortSignal.timeout(Math.max(1, Math.ceil(timeoutMs)));
  return callerSignal
    ? AbortSignal.any([callerSignal, timeoutSignal])
    : timeoutSignal;
}

async function responseJson<T>(response: Response): Promise<T> {
  const body = (await response.json().catch(() => null)) as T | null;
  if (body === null) {
    throw new DeviceAuthorizationError(
      `q5m authorization returned HTTP ${response.status}`,
      "invalid_response",
    );
  }
  return body;
}

export async function authorizeWithDeviceFlow(
  options: DeviceFlowOptions,
): Promise<string> {
  const fetch_ = options.fetch ?? globalThis.fetch;
  const sleep = options.sleep ?? defaultSleep;
  const now = options.now ?? Date.now;
  const baseUrl = options.apiUrl.replace(/\/$/, "");
  options.signal?.throwIfAborted();

  const authorizationResponse = await fetch_(`${baseUrl}/auth/device/authorization`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ client_id: options.clientId, scope: "cli" }),
    signal: boundedRequestSignal(options.signal, REQUEST_TIMEOUT_MS),
  });
  const authorizationBody = await responseJson<DeviceAuthorization & OAuthError>(
    authorizationResponse,
  );
  if (!authorizationResponse.ok) {
    throw new DeviceAuthorizationError(
      authorizationBody.error_description ?? authorizationBody.error ?? "Authorization failed",
      authorizationBody.error ?? "authorization_failed",
    );
  }
  if (
    !authorizationBody.device_code ||
    !authorizationBody.user_code ||
    !authorizationBody.verification_uri ||
    !authorizationBody.expires_in
  ) {
    throw new DeviceAuthorizationError(
      "q5m returned an incomplete device authorization",
      "invalid_response",
    );
  }

  await options.onVerification(authorizationBody);
  options.signal?.throwIfAborted();

  const expiresAt = now() + authorizationBody.expires_in * 1000;
  let intervalSeconds = Math.max(5, authorizationBody.interval ?? 5);
  while (now() < expiresAt) {
    await sleep(intervalSeconds * 1000, options.signal);
    options.signal?.throwIfAborted();
    if (now() >= expiresAt) break;
    let response: Response;
    let body: DeviceToken & OAuthError;
    try {
      response = await fetch_(`${baseUrl}/auth/device/token`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          grant_type: DEVICE_GRANT_TYPE,
          device_code: authorizationBody.device_code,
          client_id: options.clientId,
        }),
        signal: boundedRequestSignal(
          options.signal,
          Math.min(REQUEST_TIMEOUT_MS, expiresAt - now()),
        ),
      });
      body = await responseJson<DeviceToken & OAuthError>(response);
    } catch (error) {
      if (options.signal?.aborted) throw error;
      if (now() >= expiresAt) break;
      intervalSeconds = Math.min(intervalSeconds * 2, 60);
      continue;
    }
    if (response.ok) {
      if (
        typeof body.token_type === "string" &&
        body.token_type.toLowerCase() === "bearer" &&
        typeof body.access_token === "string" &&
        body.access_token.startsWith("q5m_")
      ) {
        return body.access_token;
      }
      intervalSeconds = Math.min(intervalSeconds * 2, 60);
      continue;
    }

    if (body.error === "authorization_pending") continue;
    if (body.error === "slow_down") {
      intervalSeconds += 5;
      continue;
    }
    if (response.status >= 500 || typeof body.error !== "string") {
      intervalSeconds = Math.min(intervalSeconds * 2, 60);
      continue;
    }
    throw new DeviceAuthorizationError(
      body.error_description ?? body.error ?? "Authorization failed",
      body.error ?? "authorization_failed",
    );
  }

  throw new DeviceAuthorizationError("The device code expired", "expired_token");
}

export async function revokeDeviceCredential(
  apiUrl: string,
  apiKey: string,
  fetch_: typeof globalThis.fetch = globalThis.fetch,
): Promise<void> {
  const response = await fetch_(`${apiUrl.replace(/\/$/, "")}/auth/device/token`, {
    method: "DELETE",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${apiKey}`,
    },
  });
  if (response.ok || response.status === 401) return;
  throw new DeviceAuthorizationError(
    `q5m logout returned HTTP ${response.status}`,
    "logout_failed",
  );
}

export async function openBrowser(url: string): Promise<boolean> {
  const command =
    process.platform === "darwin"
      ? { executable: "open", args: [url] }
      : process.platform === "win32"
        ? { executable: "cmd", args: ["/c", "start", "", url] }
        : { executable: "xdg-open", args: [url] };
  const { promise, resolve } = Promise.withResolvers<boolean>();
  const child = spawn(command.executable, command.args, {
    detached: true,
    stdio: "ignore",
    windowsHide: true,
  });
  child.once("spawn", () => {
    child.unref();
    resolve(true);
  });
  child.once("error", () => resolve(false));
  return promise;
}
